import React, { useState, useEffect, useCallback, useRef } from "react";
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import cn from "classnames";

import { useHttp } from "hooks/http.hook";
import Label from "components/InputsField/Label";
import InputError from "components/InputsField/InputError";

import styles from "./styles.module.scss";

const FormikCreatableAsyncSelect = (props) => {
    const { url, label, name, required, className, selectClassName, errorMode, sort = false, customFieldName } = props;

    const { t } = useTranslation();
    const { setFieldValue, setFieldTouched, values } = useFormikContext();
    const [field, meta] = useField({ name });
    const [defaultValue, setDefaultValue] = useState(null);
    const [options, setOptions] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const { request } = useHttp();
    const prevUrl = useRef();
    const prevValue = useRef();
    const prevCustomValue = useRef();

    const loadOptions = useCallback(async () => {
        setLoading(true);
        prevUrl.current = url;
        return request(url)
            .then((result) => {
                const formatedOptions = result.data.map((item) => ({
                    value: item.id,
                    label: item.value,
                }));

                let value = null;
                if (props.isMulti) {
                    value = formatedOptions.filter((option) => field.value.includes(option.value));
                } else {
                    value = formatedOptions.find((option) => option.value === field.value);
                }
                const sortedOptions = sort ? formatedOptions.sort((a, b) => a.label.localeCompare(b.label)) : formatedOptions;

                setOptions(sortedOptions);
                setDefaultValue(value);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [field.value, props.isMulti, request, sort, url]);

    const handleFocus = () => {
        if ((!options.length || prevUrl.current !== url) && url) {
            loadOptions();
        }
    };

    useEffect(() => {
        if (props.options && field.value) {
            setOptions(props.options);
            if (props.isMulti) {
                setDefaultValue(
                    props.options.filter((option) => field.value.includes(option.value)),
                );
            } else {
                setDefaultValue(props.options.find((option) => option.value === field.value));
            }
        }
    }, [props.options, props.isMulti, field.value]);

    useEffect(() => {
        // Check if there's a custom value from the custom field
        const customValue = customFieldName ? values[customFieldName] : null;

        if (customFieldName && customValue && prevCustomValue.current !== customValue) {
            prevCustomValue.current = customValue;
            setDefaultValue({ value: null, label: customValue, __isNew__: true });
            return;
        }

        if (!field.value && !customValue && prevCustomValue.current !== customValue) {
            prevCustomValue.current = customValue;
            setDefaultValue(null);
        }

        if (url && !customValue) {
            if (
                (field.value && !defaultValue) ||
                prevUrl.current !== url ||
                field.value !== prevValue.current
            ) {
                prevValue.current = field.value;
                loadOptions();
            }
        }
    }, [defaultValue, field.value, loadOptions, url, customFieldName, values]);

    const handleChange = (option, actionMeta) => {
        if (actionMeta.action === 'create-option') {
            // User created a new option
            setFieldValue(name, null);
            if (customFieldName) {
                setFieldValue(customFieldName, option.label);
            }
            setDefaultValue(option);
        } else if (option && option.__isNew__) {
            // Selected a custom option
            setFieldValue(name, null);
            if (customFieldName) {
                setFieldValue(customFieldName, option.label);
            }
            setDefaultValue(option);
        } else {
            // Selected an existing option
            if (props.isMulti) {
                setFieldValue(name, option ? option.map((item) => item.value) : null);
            } else {
                setFieldValue(name, option ? option.value : null);
            }
            if (customFieldName) {
                setFieldValue(customFieldName, "");
            }
            setDefaultValue(option);
        }
    };

    return (
        <div className={cn(styles.wrapper, className)}>
            <Label name={name} label={label} required={required} />
            <CreatableSelect
                onFocus={handleFocus}
                onBlur={() => setFieldTouched(name)}
                isSearchable={true}
                isLoading={isLoading}
                options={options}
                classNamePrefix="select"
                inputId={name}
                onChange={handleChange}
                value={defaultValue}
                loadingMessage={() => t("loading")}
                noOptionsMessage={() => t("errors.noOptions")}
                placeholder={t("defaultPlaceholder")}
                formatCreateLabel={(inputValue) => `${t("add")}: "${inputValue}"`}
                {...props}
                className={cn(styles.select, selectClassName, {
                    [styles["select--error"]]: meta.touched && meta.error,
                })}
            />
            {meta.touched && meta.error && (
                <InputError message={meta.error} mode={errorMode || "top-right"} />
            )}
        </div>
    );
};

export default FormikCreatableAsyncSelect;
